<template>
    <div class="overflow-hidden py-4 bg-white sm:shadow-sm sm:px-6 lg:px-8 dark:bg-gray-800 sm:rounded-lg">
        <div class="text-gray-900 dark:text-gray-100">
            <div class="flex justify-between items-center mb-4">
                <div v-if="showViewAll && business?.reviews_count > 0" class="text-sm">
                    <router-link
                        :to="{ name: ROUTES.BUSINESS_REVIEW, params: { id: business.uuid } }"
                        class="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300">
                    View all reviews
                    </router-link>
                </div>
            </div>

            <!-- Review Form or Message -->
            <div v-if="isCustomer" class="mb-8">
                <div v-if="existingReview && !showViewAll" class="p-4 mb-4 text-yellow-700 bg-yellow-100 rounded-lg dark:bg-yellow-900 dark:text-yellow-200">
                    <p>You have already reviewed this business. You can find your review below and edit it if you'd like to make changes.</p>
                </div>
                <ReviewForm
                    v-else
                    :business="business"
                    :existing-review="existingReview"
                />
            </div>

            <!-- Reviews List -->
            <div v-if="!reviews || reviews.length === 0" class="py-8 text-center">
                <p class="text-gray-500 dark:text-gray-400">No reviews yet.</p>
            </div>
            <div v-else>
                <h3 class="mb-4 text-lg font-semibold dark:text-white">{{ title }}</h3>
                <div class="space-y-6">
                    <Review
                        v-for="review in reviews"
                        :key="review.id"
                        :review="review"
                        :business="business" />
                </div>

                <!-- Pagination -->
                <div v-if="pagination && pagination.total > pagination.per_page" class="mt-8">
                    <nav class="flex justify-between items-center">
                        <div class="flex flex-1 justify-between sm:hidden">
                            <button
                                @click="changePage(pagination.current_page - 1)"
                                :disabled="pagination.current_page === 1"
                                class="inline-flex relative items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                                Previous
                            </button>
                            <button
                                @click="changePage(pagination.current_page + 1)"
                                :disabled="pagination.current_page === pagination.last_page"
                                class="inline-flex relative items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                                Next
                            </button>
                        </div>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import Review from '@/components/review/Review.vue';
import ReviewForm from '@/components/review/ReviewForm.vue';
import { ROUTES } from '@/router/routeNames';
import { USER_ROLES } from '@/types';

const router = useRouter();
const authStore = useAuthStore();

const props = defineProps({
    business: {
        type: Object,
        required: true
    },
    reviews: {
        type: Array,
        default: () => []
    },
    title: {
        type: String,
        default: 'Reviews'
    },
    showViewAll: {
        type: Boolean,
        default: true
    },
    existingReview: {
        type: Object,
        default: null
    },
    pagination: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['page-change']);

const isCustomer = computed(() => {
    return authStore.user &&
           authStore.user.role === USER_ROLES.CUSTOMER &&
           authStore.user.id !== props.business?.user_id;
});

const changePage = (page) => {
    emit('page-change', page);
};
</script>
